import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  createInitialValues,
  createFormValidationFn,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_HEIGHT_MEDIUM,
} from 'client/components/SrButton/SrButton';

import useAddressRegisterAutocomplete, {
  getAutocompleteAddressFields,
  getAutocompleteFieldGroups,
  getAutocompleteAddressFieldsValid,
} from './useAddressRegisterAutocomplete';
import {
  getManualAddressFieldsValid,
  getManualFieldGroups,
  getManualEntryHandler,
  useAddressRegisterManualEntry,
} from './useAddressRegisterManualEntry';
import './SrUtilityProfitAdminRegisterAddressForm.less';

const FORM_MODE_KEY_MANUAL = 'manual';
const FORM_MODE_KEY_AUTOCOMPLETE = 'autocomplete';

const getUtilityPartnerField = ({ utilityPartnerId, utilityPartnerList } = {}) => {
  return hideFieldIf(utilityPartnerId, {
    title: 'Utility Partner',
    id: 'utilityPartnerId',
    type: 'select',
    properties: {
      placeholder: '--',
      enableResetOption: true,
      choices: (utilityPartnerList || []).map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    },
    validations: ['required'],
  });
};

const getFlattenedFieldsFromGroups = ({ fieldGroups = [] } = {}) => {
  return fieldGroups.reduce((acc, group) => [...acc, ...(group.fields || [])], []);
};

const getSuccessAddressLabel = (address = {}) => {
  return address.display || 'The address';
};

const SrUtilityProfitAdminRegisterAddressForm = ({
  dispatch,
  utilityPartnerId,
  utilityPartnerList,
  afterSubmit,
  onSuccessModalClose,
  isManualAddressEntry,
  setIsManualAddressEntry,
}) => {
  const [successAddressLabel, setSuccessAddressLabel] = useState(null);
  const handleRegisterSuccess = (address) => {
    setSuccessAddressLabel(getSuccessAddressLabel(address));
  };
  const handleCloseSuccessModal = () => {
    setSuccessAddressLabel(null);
    if (onSuccessModalClose) {
      onSuccessModalClose();
    }
  };

  const autocompleteController = useAddressRegisterAutocomplete({
    dispatch,
    afterSubmit,
    utilityPartnerId,
    onRegisterSuccess: handleRegisterSuccess,
  });
  const manualEntryController = useAddressRegisterManualEntry({
    dispatch,
    afterSubmit,
    utilityPartnerId,
    onRegisterSuccess: handleRegisterSuccess,
  });
  const { onSubmit, submitErrorMessage } = isManualAddressEntry
    ? manualEntryController
    : autocompleteController;

  const utilityPartnerField = getUtilityPartnerField({
    utilityPartnerId,
    utilityPartnerList,
  });
  const initialAutocompleteField = getAutocompleteAddressFields({
    formValues: { utilityPartnerId },
  })[0];
  const { fieldGroups: initialFieldGroups } = isManualAddressEntry
    ? getManualFieldGroups({ utilityPartnerField })
    : getAutocompleteFieldGroups({
        utilityPartnerField,
        autocompleteField: initialAutocompleteField,
      });
  const initialFormFields = getFlattenedFieldsFromGroups({ fieldGroups: initialFieldGroups });

  return (
    <>
      <Formik
        key={isManualAddressEntry ? FORM_MODE_KEY_MANUAL : FORM_MODE_KEY_AUTOCOMPLETE}
        initialValues={createInitialValues({
          formFields: initialFormFields,
          defaultValues: {
            utilityPartnerId,
          },
        })}
        enableReinitialize
        validate={createFormValidationFn(initialFormFields)}
        onSubmit={onSubmit}
        render={(formikBag) => {
          const formValues = formikBag.values;
          const addressFieldsValid = getAutocompleteAddressFieldsValid(formValues);
          const manualAddressFieldsValid = getManualAddressFieldsValid(formValues);

          const handleManualEntry = setIsManualAddressEntry
            ? getManualEntryHandler({
                setIsManualAddressEntry,
                formikBag,
                initialFormFields,
                utilityPartnerId,
                createInitialValues,
              })
            : undefined;

          const autocompleteAddressField = getAutocompleteAddressFields({
            formValues,
            ...(handleManualEntry ? { onManualEntry: handleManualEntry } : {}),
          })[0];

          const { fieldGroups } = isManualAddressEntry
            ? getManualFieldGroups({ utilityPartnerField })
            : getAutocompleteFieldGroups({
                utilityPartnerField,
                autocompleteField: autocompleteAddressField,
              });

          return (
            <Form>
              {fieldGroups.map((fieldGroup) => (
                <div
                  key={`${fieldGroup.layoutClassName}-${fieldGroup.fields
                    .map((field) => field.id)
                    .join('-')}`}
                  styleName={fieldGroup.layoutClassName}
                >
                  {fieldGroup.fields.map((formField) => (
                    <SrFormikField
                      key={formField.id}
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  ))}
                </div>
              ))}
              <SrBox padding="28px 0 12px">
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  disabled={
                    !formikBag.isValid ||
                    !formikBag.dirty ||
                    (isManualAddressEntry ? !manualAddressFieldsValid : !addressFieldsValid)
                  }
                  heightType={SR_BUTTON_HEIGHT_MEDIUM}
                  hasBoxShadow
                  type="submit"
                >
                  {utilityPartnerId ? 'Submit' : 'Register Address'}
                </SrButton>
              </SrBox>
              {submitErrorMessage && <SrErrorMessage>{submitErrorMessage}</SrErrorMessage>}
            </Form>
          );
        }}
      />
      {successAddressLabel && (
        <SrSimpleMessageModal
          iconUrl="/images/checkmark-circle-large-green.svg"
          headline="Success!"
          underline
          message={
            <SrBox maxWidth="280px" margin="0 auto">
              <SrText font="greycliff" weight="700" fontSize="16px" display="block">
                {successAddressLabel}
              </SrText>
              <SrText fontSize="16px" display="block">
                has been registered successfully.
              </SrText>
            </SrBox>
          }
          srMultiButtons={[
            {
              buttonText: 'Continue',
              buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
              onButtonClick: handleCloseSuccessModal,
            },
          ]}
          onClose={handleCloseSuccessModal}
          maxWidth={360}
          maxIconHeight="100"
          maxIconWidth="100"
        />
      )}
    </>
  );
};

SrUtilityProfitAdminRegisterAddressForm.propTypes = {
  utilityPartnerId: PropTypes.number,
  utilityPartnerList: PropTypes.array,
  afterSubmit: PropTypes.func,
  onSuccessModalClose: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  isManualAddressEntry: PropTypes.bool,
  setIsManualAddressEntry: PropTypes.func,
};

SrUtilityProfitAdminRegisterAddressForm.displayName = 'SrUtilityProfitAdminRegisterAddressForm';

export default SrUtilityProfitAdminRegisterAddressForm;
